import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectEdit } from './hooks/useProjectEdit';
import { ProjectBasicInfo } from '../new/components/ProjectBasicInfo';
import { ProjectTimelineInfo } from '../new/components/ProjectTimelineInfo';
import { ProjectFinancialInfo } from '../new/components/ProjectFinancialInfo';
import { ProjectAttachments } from '../new/components/ProjectAttachments';
import { ProjectSidebarInfo } from '../new/components/ProjectSidebarInfo';
import { ProjectTeamInfo } from '../new/components/ProjectTeamInfo';

export function ProjectEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { form, updateForm, isLoading, isSaving, saveProject } = useProjectEdit(id || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    try {
      await saveProject(form);
      navigate(`/projects/${id}`);
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="p-6 space-y-6 animate-pulse bg-slate-50/50 min-h-screen">
        <div className="h-48 rounded-md bg-white border border-slate-100" />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8 space-y-6">
            <div className="h-64 rounded-md bg-white border border-slate-100" />
            <div className="h-48 rounded-md bg-white border border-slate-100" />
          </div>
          <div className="col-span-4 h-96 rounded-md bg-white border border-slate-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      {/* Header section */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(`/projects/${id}`)}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all  active:scale-95"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-xl font-normal text-slate-900 tracking-tight">Chỉnh sửa dự án</h1>
              <p className="text-xs lg:text-sm font-normal text-slate-500">Cập nhật thông tin vận hành và tài chính của dự án.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Form */}
      <form id="project-form" onSubmit={handleSubmit} className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Column: Main form blocks (8/12) */}
            <div className="lg:col-span-8 space-y-6">
              <ProjectBasicInfo form={form} updateForm={updateForm} />
              <ProjectTimelineInfo form={form} updateForm={updateForm} />
              <ProjectFinancialInfo form={form} updateForm={updateForm} />
              <ProjectTeamInfo form={form} updateForm={updateForm} />
              <ProjectAttachments 
                attachments={form.attachments} 
                onRemove={(url) => updateForm({ attachments: (form.attachments || []).filter((a: string) => a !== url) })}
              />
            </div>

            {/* Right Column: Sidebar blocks (4/12) */}
            <div className="lg:col-span-4 sticky top-0">
              <ProjectSidebarInfo 
                form={form} 
                updateForm={updateForm} 
                isSaving={isSaving}
                onCancel={() => navigate(`/projects/${id}`)}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ProjectEditPage;
